import { useAuth } from '../firebase/FirebaseContext';
import { useAlerts } from '../hooks/useAlerts';
import { useAuthSnapshot } from '../hooks/useAuthSnapshot';
import { useTotalLikedCount } from '../hooks/useTotalLikedCount';
import { hubAlertRows } from './hubAlerts';

/**
 * The Hub mobile tab bar's You-tab dot — TWO no-number red dots in one tab-bar
 * position, mutually exclusive by auth state (THE-973 / THE-967 PR 3, PRD §2
 * p.17 for the guest dot and M6 p.15 + QA p.22 for the signed-in one).
 *
 *   'guest'         — she has saved something but has no account yet.
 *   'notifications' — she HAS an account and there is >= 1 notification row.
 *   null            — neither.
 *
 * WHY A NEW HOOK AND NOT `useYouTabBadge`. That hook returns a COUNT and the
 * Hub needs a presence, but the real reason is its consumer list: it feeds the
 * legacy stack only (`MobileTabBarMount`, `CalendarPage`, `TopicHubPage`,
 * `YouPage`, `GiftGuidesPage`) and `HubNavigationView` has never referenced it.
 * Widening it would push the Hub's rules onto five non-Hub surfaces the PRD
 * marks NO CHANGE, and it is also the file open PR #427 edits. So: new
 * Hub-local state, and the two bodies of work stay disjoint.
 *
 * THE TWO DOTS ARE NOT THE SAME SIGNAL WITH TWO AUDIENCES, which is why this
 * returns a tagged value rather than a boolean:
 *
 *  - The GUEST dot points at an account she does not have: it appears at her
 *    FIRST save (not at three, which is today's non-Hub `SIGN_IN_NUDGE_THRESHOLD`
 *    on `HeaderAccountMenu`'s avatar — deliberately NOT reused, that threshold
 *    stays exactly as it is on every non-Hub surface) and it clears the moment
 *    she has one. Nothing can dismiss it; getting the account is the only exit.
 *  - The SIGNED-IN dot points at rows on the You tab, so it is `rows.length >= 1`
 *    off the ONE row model (`hubAlerts.ts`) that also feeds the list and PR 1's
 *    desktop avatar number. Dismissing the last row drops the count to zero and
 *    the dot goes with it — one mechanism, three surfaces. There is no
 *    "clear on tab open" rule in the PRD and this deliberately does not invent
 *    one: a dot that cleared on open would disagree with the list it points at.
 *
 * `isPermanent` is the switch between them, so the two can never show together
 * and no state exists where a dot means "either of two different things".
 *
 * DESKTOP NEVER SEES EITHER, and that falls out of the markup rather than a
 * media query here: the You tab only exists in `HubNavigationView`'s mobile bar
 * (its desktop nav filters `you` out), so the guest's door on desktop stays the
 * header's `Log in` and a signed-in account keeps today's number-on-avatar.
 *
 * UNREADY AUTH SHOWS NOTHING. `ready === false` is "we do not know who she is
 * yet", and both dots are claims about a specific person — a dot that appeared
 * for a frame and then changed meaning is worse than one that arrives a beat
 * late.
 */
export type HubYouTabDot = 'guest' | 'notifications' | null;

export function useHubYouTabDot(): HubYouTabDot {
  const auth = useAuth();
  const { uid, isPermanent, ready, emailVerified } = useAuthSnapshot(auth);
  const totalLiked = useTotalLikedCount();
  const { alerts } = useAlerts();
  // `emailVerified` straight off the snapshot since THE-972 (#757) landed it
  // there — the `useHubEmailVerified` bridge this used to call existed only to
  // let PR 3 ship before PR 2, and is deleted. `!!auth.currentUser?.email`
  // because an account with no address has nothing to confirm.
  const needsEmailConfirm = isPermanent && !emailVerified && !!auth.currentUser?.email;

  if (!ready || !uid) return null;
  if (!isPermanent) return totalLiked >= 1 ? 'guest' : null;
  return hubAlertRows({ alerts, needsEmailConfirm }).length >= 1 ? 'notifications' : null;
}
